import { useEffect, useMemo, useState } from "react";
import Modal from "../../layouts/Modal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import {
  ICreateShopifyProfileFromDraftRequest,
  IPreviewShopifyProfileFromEbayRequest,
  IShopifyProfileDraft,
  IShopifyStore,
} from "../../models/ShopifyShippingPolicy";
import { IEbayItem } from "../../models/EbayOffer";

const emptyOption = "";

interface CreateShopifyProfileModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  stores: IShopifyStore[];
  ebayPolicies: IEbayItem[];
  previewLoading: boolean;
  saveLoading: boolean;
  onPreview: (req: IPreviewShopifyProfileFromEbayRequest) => Promise<IShopifyProfileDraft | null>;
  onSave: (req: ICreateShopifyProfileFromDraftRequest) => Promise<boolean>;
}

export default function CreateShopifyProfileModal({
  open,
  setOpen,
  stores,
  ebayPolicies,
  previewLoading,
  saveLoading,
  onPreview,
  onSave,
}: CreateShopifyProfileModalProps) {
  const [selectedStoreId, setSelectedStoreId] = useState<string>(emptyOption);
  const [selectedPolicyId, setSelectedPolicyId] = useState<string>(emptyOption);
  const [profileName, setProfileName] = useState<string>("");
  const [draft, setDraft] = useState<IShopifyProfileDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setSelectedStoreId(stores[0]?.id ?? emptyOption);
    setSelectedPolicyId(emptyOption);
    setProfileName("");
    setDraft(null);
    setError(null);
  }, [open, stores]);

  const selectedPolicy = useMemo(
    () => ebayPolicies.find((x) => x.id === selectedPolicyId),
    [ebayPolicies, selectedPolicyId]
  );

  const handlePrefill = async () => {
    setError(null);
    if (!selectedStoreId || !selectedPolicyId) return;

    const result = await onPreview({
      shopifyStoreId: selectedStoreId,
      fulfillmentPolicyId: selectedPolicyId,
    });

    if (!result) return;
    setDraft(result);
    setProfileName(result.deliveryProfileName ?? "");
  };

  const handleSave = async () => {
    setError(null);
    if (!selectedStoreId || !selectedPolicy || !profileName.trim()) return;

    const success = await onSave({
      shopifyStoreId: selectedStoreId,
      fulfillmentPolicyId: selectedPolicy.id,
      fulfillmentPolicyName: selectedPolicy.name,
      deliveryProfileName: profileName.trim(),
    });

    if (success)
    {
      setOpen(false);
      return;
    }

    setError("Failed to create Shopify profile. Check required fields and try again.");
  };

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-4xl">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Create Shopify shipping profile</h2>
          <span className="text-xs text-gray-500">Prefill from eBay policy</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Shopify store</p>
            <select
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value={emptyOption}>Select store</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.domain}
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">eBay fulfillment policy</p>
            <select
              value={selectedPolicyId}
              onChange={(e) => setSelectedPolicyId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value={emptyOption}>Select policy</option>
              {ebayPolicies.map((policy) => (
                <option key={policy.id} value={policy.id}>
                  {policy.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <ButtonInput
            label="Prefill from eBay Policy"
            isSubmit={false}
            onClick={handlePrefill}
            classes={undefined}
            disabled={!selectedStoreId || !selectedPolicyId}
            isLoading={previewLoading}
          />
        </div>

        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Shopify delivery profile name</p>
          <input
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="Enter profile name"
          />
        </div>

        {draft?.suggestedRates?.length ? (
          <div className="rounded-lg border border-gray-200 p-3">
            <p className="text-sm font-semibold text-gray-800 mb-2">Mapped shipping hints from eBay</p>
            <div className="overflow-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">Type</th>
                    <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">Carrier</th>
                    <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">Service</th>
                    <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {draft.suggestedRates.slice(0, 8).map((rate, idx) => (
                    <tr key={`${rate.serviceCode}-${idx}`}>
                      <td className="px-2 py-1 text-xs text-gray-800">{rate.optionType || rate.costType || "-"}</td>
                      <td className="px-2 py-1 text-xs text-gray-800">{rate.carrierCode || "-"}</td>
                      <td className="px-2 py-1 text-xs text-gray-800">{rate.serviceCode || "-"}</td>
                      <td className="px-2 py-1 text-xs text-gray-800">
                        {rate.freeShipping ? "Free" : rate.shippingCost ?? "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {draft?.unmappedFields?.length ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-semibold text-amber-900 mb-1">Manual review needed</p>
            <ul className="list-disc pl-5 text-sm text-amber-800 space-y-1">
              {draft.unmappedFields.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <ButtonInput
            label="Cancel"
            isSubmit={false}
            onClick={() => setOpen(false)}
            classes="!bg-gray-200 !text-gray-700 hover:!bg-gray-300"
          />
          <ButtonInput
            label="Create Profile + Link Mapping"
            isSubmit={false}
            onClick={handleSave}
            classes={undefined}
            disabled={!selectedStoreId || !selectedPolicyId || !profileName.trim()}
            isLoading={saveLoading}
          />
        </div>
      </div>
    </Modal>
  );
}
